// src/sim/ai/perception.ts — P4 perception builder.
//
// Pitfall 4 forcing function: AI controllers receive ONLY the Perception object
// returned by this function, never raw WorldState. Every field an AI branch
// could need is exposed here.
//
// Invariants:
//   - Pure function: no mutation of inputs, no side effects.
//   - Fail-safe: missing characterId profile → returns minimal Perception with
//     empty visible[] / heard[] and never throws.
//   - visible[] is sorted by entity id (localeCompare) for deterministic downstream
//     processing (Pitfall 10 avoidance).
//   - Hunter role uses a pre-computed FOV bitset (agentFovBitset); other roles
//     use Euclidean radius + isBlockedByWall LOS (D-09 pattern).
//
// Source: .planning/phases/04-ai/04-02-PLAN.md Task 2, 04-RESEARCH.md Pattern 1.

import type { Entity, WorldState } from '../../shared/types/world';
import type { MapData } from '../../shared/types/map';
import type { Perception, PerceptionEntity, PerceptionSelf, PerceptionSound } from '../../shared/types/perception';
import { hasProfile, getProfile } from '../characters/profiles';
// P7 POL-06 / D-12: quietness-hearing multiplier read through the tuning cache.
import { getTuning } from '../content/cache';
import { sightabilityOf } from '../perception/sightability';
import { lightLevelAt } from '../lighting/light-level';
import { tileAt } from '../map/grid';
// zoneAt import removed: the caughtHidersAtBase check no longer uses a
// position-based zone lookup (see comment in the loop below for rationale).
import {
  P4_MIN_VISIBLE_LIGHT,
  P4_HIDER_VISION_RADIUS_TILES,
  P4_AI_AMBIENT_LIGHT_FLOOR,
} from './constants';

// ---------------------------------------------------------------------------
// P6 D-10 quietness-hearing hook (golden-hash-stable).
// ---------------------------------------------------------------------------
// Quietness modulates AI audibility but is pinned at multiplier=1 in P6 — the
// multiplication is a no-op so the existing P4 hash (2dfba10c...) stays
// byte-identical. P7 POL-06 / D-12: the multiplier is now admin-editable via
// getTuning().quietnessHearingMult (default 1 in cache.ts TUNING_DEFAULTS is the
// single source of truth — the former private const lived here).
// ---------------------------------------------------------------------------
// Bitset helper (hunter FOV)
// ---------------------------------------------------------------------------

/** Test whether tile (tx, ty) is set in a packed Uint32Array bitset. */
function isInBitset(bitset: Uint32Array, tx: number, ty: number, widthInTiles: number): boolean {
  const idx = ty * widthInTiles + tx;
  const wordIdx = idx >>> 5;      // Math.floor(idx / 32)
  const bitIdx = idx & 31;        // idx % 32
  if (wordIdx >= bitset.length) return false;
  return (bitset[wordIdx]! & (1 << bitIdx)) !== 0;
}

// ---------------------------------------------------------------------------
// Wall-LOS helper (hider / dog / adult roles)
// Identical body to lightLevelAt's isBlockedByWall — copied per plan to keep
// perception.ts independent of the light-level module's internals.
// ---------------------------------------------------------------------------

function isBlockedByWall(a: { x: number; y: number }, b: { x: number; y: number }, map: MapData): boolean {
  const steps = Math.ceil(Math.hypot(b.x - a.x, b.y - a.y) / map.tileWidth);
  for (let i = 1; i < steps; i++) {
    const t = i / steps;
    const tx = Math.floor((a.x + (b.x - a.x) * t) / map.tileWidth);
    const ty = Math.floor((a.y + (b.y - a.y) * t) / map.tileHeight);
    if (tileAt(map.grid, tx, ty) === 1) return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Sound input type (mirrors the plan's recentSoundEvents shape)
// ---------------------------------------------------------------------------

interface SoundEvent {
  kind: 'shout' | 'dog-bark';
  sourceId: string;
  position: { x: number; y: number };
  tick: number;
}

// ---------------------------------------------------------------------------
// buildPerception
// ---------------------------------------------------------------------------

/**
 * Build the sensory snapshot for a single AI agent.
 *
 * @param self                  The agent entity.
 * @param world                 Current immutable WorldState snapshot.
 * @param mapData               Map static data.
 * @param agentFovBitset        Pre-computed FOV bitset for hunters; null for non-hunters.
 * @param perAgentRng           Per-entity seeded RNG sub-stream (D-06 alea sub-stream).
 * @param previousAwarenessMap  Per-target awareness values from the last perception tick.
 * @param recentSoundEvents     Sound events emitted since the last perception tick.
 */
export function buildPerception(
  self: Entity,
  world: WorldState,
  mapData: MapData,
  agentFovBitset: Uint32Array | null,
  perAgentRng: () => number,
  previousAwarenessMap: ReadonlyMap<string, number>,
  recentSoundEvents: ReadonlyArray<SoundEvent>,
): Perception {
  // WR-09 fix: compute totalHiders once (used in both the fail-safe branch and
  // the normal branch below). Count hiders across all entities — the hider
  // archetype scorer divides caughtAtBase / totalHiders for the liberator
  // branch so getting the current roster size right matters when Benny is
  // unlocked (5 hiders) or after catches.
  let totalHiders = 0;
  // Plan 06-13 (UAT Gap 7): count hiders parked at the plinth — caught and
  // escortedBy null (drop-off complete). The zoneAt check was originally
  // included to confirm the hider is "inside the plinth zone," but the
  // parkedHaloPosition formula in drop-off.ts places hiders at
  //   touchRadius = ringRadius (48 px) + charRadius (16 px) = 64 px
  // from the ring centre — OUTSIDE the plinth zone polygon which covers only
  // the ring's interior (radius ≈ 48 px). The zoneAt check therefore always
  // returned false in real play, making caughtHidersAtBase permanently 0 and
  // preventing the liberation branch from ever winning (AI2-04 device failure,
  // debug session p11-uat-round4-fixes 2026-07-05).
  //
  // Fix: use only status='caught' && escortedBy===null, which exactly matches
  // drop-off.ts's own "alreadyParked" filter. The lifecycle guarantees that
  // escortedBy is cleared to null ONLY by applyDropOff (which also parks the
  // hider near the ring), so no position check is needed.
  let caughtHidersAtBase = 0;
  for (const other of Object.values(world.entities)) {
    if (other.role === 'hider') {
      totalHiders++;
      if (other.status === 'caught' && other.escortedBy === null) {
        caughtHidersAtBase++;
      }
    }
  }

  // Fail-safe: unknown characterId → return minimal Perception (no throw).
  if (!hasProfile(self.characterId)) {
    const selfSnap: PerceptionSelf = {
      id: self.id,
      characterId: self.characterId,
      position: { ...self.position },
      facing: self.facing,
      role: self.role,
      status: self.status,
      torchOn: self.torchOn,
      stamina: self.stamina,
      fear: self.fear,
      awareness: new Map(),
      // Plan 06-13: surface even on fail-safe so downstream consumers never
      // read undefined for an entity that happens to have an unknown profile.
      recentDropOffTick: self.recentDropOffTick ?? null,
      // Debug session freed-hiders-dont-disperse (2026-04-28): mirror.
      freedImmunityUntilTick: self.freedImmunityUntilTick ?? null,
      // Flee-from-base (2026-06-12): surface the transient flee deadline.
      fleeFromBaseUntilTick: self.fleeFromBaseUntilTick ?? null,
    };
    return {
      self: selfSnap,
      visible: [],
      heard: [],
      memory: [],
      map: mapData,
      round: {
        phase: world.round.phase,
        roundNumber: world.round.roundNumber,
        tickInRound: world.tick - (world.round.startTick ?? 0),
        totalHiders,
        caughtHidersAtBase,
      },
      rng: perAgentRng,
      tick: world.tick,
    };
  }

  const profile = getProfile(self.characterId);

  // ---------------------------------------------------------------------------
  // Build visible[] — entities in FOV + passing light gate, sorted by id.
  // ---------------------------------------------------------------------------

  const visibleList: PerceptionEntity[] = [];

  // Sort entity ids for deterministic iteration (Pitfall 10).
  const otherIds = Object.keys(world.entities).sort((a, b) => a.localeCompare(b));

  for (const otherId of otherIds) {
    if (otherId === self.id) continue;

    const other = world.entities[otherId];
    if (!other) continue;

    // FOV test.
    let inFov = false;
    if (self.role === 'hunter' && agentFovBitset !== null) {
      // Hunter with a pre-computed bitset.
      const tx = Math.floor(other.position.x / mapData.tileWidth);
      const ty = Math.floor(other.position.y / mapData.tileHeight);
      inFov = isInBitset(agentFovBitset, tx, ty, mapData.widthInTiles);
    } else if (self.role === 'hunter') {
      // Hunter with deferred per-entity FOV (agentFovBitset not yet computed — tick.ts
      // Stage 16 hardcodes null until per-entity torch-cone FOV is implemented).
      // Use the awareness max range (matching Stage 17 accumulator) with wall LOS so that
      // a hider that triggers the awareness accumulator also appears in p.visible and
      // can be chased directly. Without this, hunters were capped at the 6-tile hider
      // radius, producing investigate-sighting instead of chase for hiders at 7-13 tiles.
      // Debug session hunter-no-chase-facing (2026-07-04).
      const radiusPx = getTuning().awarenessMaxRangeTiles * mapData.tileWidth;
      const distPx = Math.hypot(other.position.x - self.position.x, other.position.y - self.position.y);
      if (distPx <= radiusPx && !isBlockedByWall(self.position, other.position, mapData)) {
        inFov = true;
      }
    } else {
      // Hider / dog / adult: radius + wall LOS.
      const radiusPx = P4_HIDER_VISION_RADIUS_TILES * mapData.tileWidth;
      const distPx = Math.hypot(other.position.x - self.position.x, other.position.y - self.position.y);
      if (distPx <= radiusPx && !isBlockedByWall(self.position, other.position, mapData)) {
        inFov = true;
      }
    }

    if (!inFov) continue;

    // Light gate. UAT Test 2 follow-up: apply P4_AI_AMBIENT_LIGHT_FLOOR to
    // model generic outdoor/moonlight ambient so hunters aren't blind across
    // the ~95% of the map that has no authored light (deferred Phase-4
    // per-hunter-torch-FOV integration would replace this).
    const rawLight = lightLevelAt(
      other.position,
      mapData,
      world.lightsState,
      [],
      world.tick,
    );
    const light = Math.max(rawLight, P4_AI_AMBIENT_LIGHT_FLOOR);
    if (light < P4_MIN_VISIBLE_LIGHT) continue;

    // Sightability = sightabilityOf(entity, mapData) × light.
    const sight = sightabilityOf(other, mapData);
    const sightability = sight * light;

    const distPx = Math.hypot(other.position.x - self.position.x, other.position.y - self.position.y);

    visibleList.push({
      id: other.id,
      position: { ...other.position },
      facing: other.facing,
      role: other.role,
      status: other.status,
      torchOn: other.torchOn,
      sightability,
      distance: distPx,
      // Debug session cascade-prevention (2026-04-28): expose so the hunter's
      // chase action can skip immune targets — see actions.ts filters.
      freedImmunityUntilTick: other.freedImmunityUntilTick ?? null,
    });
  }

  // visible[] is already in localeCompare id order because we iterated sorted ids.

  // ---------------------------------------------------------------------------
  // Build heard[] — filter recentSoundEvents by hearing radius.
  // ---------------------------------------------------------------------------

  const hearRangePx = profile.hear.base * mapData.tileWidth;
  const quietnessHearingMult = getTuning().quietnessHearingMult;
  const heardList: PerceptionSound[] = [];

  for (const ev of recentSoundEvents) {
    const distPx = Math.hypot(ev.position.x - self.position.x, ev.position.y - self.position.y);
    const audibility = Math.max(0, 1 - distPx / hearRangePx) * quietnessHearingMult;
    if (audibility <= 0) continue;
    heardList.push({
      kind: ev.kind,
      sourceId: ev.sourceId,
      position: { ...ev.position },
      audibility,
      tick: ev.tick,
    });
  }

  // ---------------------------------------------------------------------------
  // self snapshot
  // ---------------------------------------------------------------------------

  const selfSnap: PerceptionSelf = {
    id: self.id,
    characterId: self.characterId,
    position: { ...self.position },
    facing: self.facing,
    role: self.role,
    status: self.status,
    torchOn: self.torchOn,
    stamina: self.stamina,
    fear: self.fear,
    awareness: previousAwarenessMap,
    // Plan 06-13 (UAT Gap 5): wire the entity's recentDropOffTick into
    // perception so scoreHunterBranches can apply the post-drop-off
    // patrol-base penalty.
    recentDropOffTick: self.recentDropOffTick ?? null,
    // Debug session freed-hiders-dont-disperse (2026-04-28): wire into
    // perception so scoreHiderBranches can compute the post-liberation
    // liberate-suppression window AND so the AI controller's D-32 / AI-16
    // ZERO_INTENT immunity gate (which previously read this field via an
    // optional-cast that always returned undefined) actually works.
    freedImmunityUntilTick: self.freedImmunityUntilTick ?? null,
    // Flee-from-base (2026-06-12): surface the transient flee deadline so the
    // AIController can short-circuit into a radial-away-from-base move.
    fleeFromBaseUntilTick: self.fleeFromBaseUntilTick ?? null,
  };

  // ---------------------------------------------------------------------------
  // memory — team blackboard entries
  // ---------------------------------------------------------------------------

  const teamKey = self.role === 'hunter' ? 'hunter' : 'hider';
  const memory = world.teamBlackboards[teamKey].entries;

  // ---------------------------------------------------------------------------
  // round
  // ---------------------------------------------------------------------------

  const tickInRound = world.tick - (world.round.startTick ?? 0);

  return {
    self: selfSnap,
    visible: visibleList,
    heard: heardList,
    memory,
    map: mapData,
    round: {
      phase: world.round.phase,
      roundNumber: world.round.roundNumber,
      tickInRound,
      totalHiders,
      caughtHidersAtBase,
    },
    rng: perAgentRng,
    tick: world.tick,
  };
}
